import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Grid,
} from '@material-ui/core';
import {
  Panel,
  PreventTransitionPrompt,
  useDialogs,
} from '@michelin/acid-components';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import { saveCommercialTireServices, useCommercialTireServices } from 'hooks/useCommercialServices';
import { HelpOutline } from '@material-ui/icons';
import { useSnackbar } from 'notistack';
import usePermissionsSPP from 'hooks/usePermissionsSPP';
import { GenericActionButtonsMassUpdate } from 'components/GenericActionButtons/GenericActionButtonsMassUpdate';
import { GenericActionButtons } from 'components/GenericActionButtons/GenericActionButtons';
import { CommercialTireServices, commercialTireServicesLabels } from './CommercialServicesFields';
import { FullChecboxPanel } from './FullCheckboxTools';
import { CheckboxGroup } from './CheckboxGroup';
import ModalHelp from './ModalHelp';
import CopyServices from '../CopyServices/CopyServices';

interface Props {
  services: CommercialTireServices,
  action: 'edit' | 'view' | 'add',
  fromMassUpdate?: boolean,
}

function CommercialServicesPanel(props: Props) {
  const { t } = useTranslation();
  const permissions = usePermissionsSPP();
  const { stNumber } = useParams<{ stNumber: string, btNumber: string }>();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const {
    formState, getValues, reset, control, watch, setValue, handleSubmit,
  } = useForm({ defaultValues: props.services });
  const queryClient = useQueryClient();
  const saveMutation = useMutation(saveCommercialTireServices, {
    onSuccess: () => queryClient.invalidateQueries(['commercial-tire-services', stNumber]),
  });
  const { errorDialog } = useDialogs();
  const readOnly: boolean = props.action === 'view';

  const [open, setOpen] = useState(false);
  const [type, setType] = useState<any>('tire');
  const countryCode = (permissions.location?.customer_country || 'us').toLocaleLowerCase();

  useEffect(() => {
    if (props.action === 'view') reset();
  }, [props.action, reset]);

  useEffect(() => {
    reset(props.services);
  }, [props.services, reset]);

  const save = async () => {
    try {
      if (!formState.isDirty) return;
      await saveMutation.mutateAsync({ stNumber, data: getValues() });
      reset(getValues());
      history.push('view');
      enqueueSnackbar(t('Commercial Tire Services information saved.'), { variant: 'success', anchorOrigin: { vertical: 'top', horizontal: 'center' } });
    } catch (error) {
      errorDialog(
        t('Please check your connection and try again.\nIf the issue persist, please contact support').split('\n'),
        t('Error saving Commercial Products Data.'),
      );
    }
  };

  return (
    <>
      <Grid container spacing={2}>
        {(props.fromMassUpdate || false) ? (
          <GenericActionButtonsMassUpdate
            isDirty={formState.isDirty}
            getValues={getValues}
            reset={reset}
            handleSubmit={handleSubmit}
            validateAction="mass_st_update.update"
          />
        )
          : (
            <GenericActionButtons
              isDirty={formState.isDirty}
              isSaving={saveMutation.isLoading}
              onSave={save}
              validateAction="location_information.update"
              readOnly={readOnly}
              additionalEditButtons={[
                <CopyServices
                  key="copyService"
                  getServicesHook={useCommercialTireServices}
                  setValue={setValue}
                  type="tires"
                />]}
            />
          )}
        <ModalHelp type={type} open={open} onClose={() => setOpen(false)} />
        <Grid item xs={12}>
          <Panel
            title={t('Tire Services')}
            style={{ minWidth: 300 }}
            icon={(
              <HelpOutline onClick={() => {
                setType('tire');
                setOpen(!open);
              }}
              />
            )}
          >
            <Grid container spacing={2}>
              <FullChecboxPanel
                getValues={getValues}
                control={control}
                readOnly={readOnly}
                labels={commercialTireServicesLabels}
                setValue={setValue}
                watch={watch}
                groups={[
                  {
                    label: t('Lt/Md Duty'),
                    cols: [
                      { label: t('In Bay'), path: 'in_bay.light_medium_duty' },
                      { label: t('Mobile'), path: 'mobile.light_medium_duty' },
                    ],
                  },
                  {
                    label: t('Heavy Duty'),
                    cols: [
                      { label: t('In Bay'), path: 'in_bay.heavy_duty' },
                      { label: t('Mobile'), path: 'mobile.heavy_duty' },
                    ],
                  },
                ]}
                items={[
                  { key: 'mounted_wheel_program' },
                  { key: 'nail_hole_repair' },
                  { key: 'tire_balancing' },
                  { key: 'tire_inspections' },
                  { key: 'tire_mounting' },
                  { key: 'tire_repairs' },
                  { key: 'tire_rotations' },
                  { key: 'tpms_reset_and_replacement' },
                  { key: 'vehicle_alignment' },
                ]}
              />
            </Grid>
          </Panel>
        </Grid>
        {countryCode === 'us' && (
          <Grid item xs={12} md={6}>
            <CheckboxGroup
              title={t('TIA Certified Technicians (OSHA Compliant)')}
              cols={12}
              getValues={getValues}
              control={control}
              readOnly={readOnly}
              setValue={setValue}
              labels={commercialTireServicesLabels}
              watch={watch}
              items={[
                { key: 'tia_certified_technicians.training_200.ats' },
                { key: 'tia_certified_technicians.training_200.cts' },
                { key: 'tia_certified_technicians.certificate_or_equivalent_300.ats' },
              ]}
            />
          </Grid>
        )}
        <Grid item xs={12} md={6}>
          <CheckboxGroup
            title={t('Wheel Refurbishing')}
            cols={12}
            getValues={getValues}
            control={control}
            readOnly={readOnly}
            setValue={setValue}
            labels={commercialTireServicesLabels}
            watch={watch}
            items={[
              { key: 'wheel_refurbishing.powder_coating' },
              { key: 'wheel_refurbishing.polishing' },
            ]}
          />
        </Grid>
      </Grid>
      <PreventTransitionPrompt
        when={formState.isDirty}
      />
    </>
  );
}

export default CommercialServicesPanel;
